import { RefreshCw, Star } from 'lucide-react'

import Button from '@/components/UiElements/Button'

type OnboardingProgressProps = {
  completedCount: number
  totalCount: number
  progress: number
  isComplete: boolean
  isLoading: boolean
  storeName?: string
  lastCheckedAt: Date | null
  onRefresh: () => void
}

const OnboardingProgress = ({
  completedCount,
  totalCount,
  progress,
  isComplete,
  isLoading,
  storeName,
  lastCheckedAt,
  onRefresh,
}: OnboardingProgressProps) => (
  <section
    className={`overflow-hidden rounded-lg border transition-colors ${
      isComplete
        ? 'border-primary-500 bg-primary-500'
        : 'border-gray-200 bg-gray-150'
    }`}
  >
    <div className="flex flex-col gap-xl p-md">
      <div className="flex min-w-0 flex-col gap-xs">
        <h2 className={`text-title-xl ${isComplete ? 'text-white' : 'text-gray-900'}`}>
          {isComplete ? `${storeName || 'Your store'} is ready` : `Build ${storeName || 'your store'} step by step`}
        </h2>
        <p className={`max-w-[620px] text-text-md leading-relaxed ${isComplete ? 'text-white' : 'text-gray-600'}`}>
          {isComplete
            ? 'Every onboarding task has been detected automatically. You can keep managing the store from the main menu.'
            : 'Follow the recommended order below. Completed work is checked automatically from your store data.'}
        </p>
      </div>

      <div className={`flex w-full flex-col gap-md border-t pt-md ${isComplete ? 'border-white/40' : 'border-gray-300'}`}>
        <div
          role="progressbar"
          aria-label="Get Started progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          className="flex flex-wrap items-center gap-xs"
        >
          {Array.from({ length: totalCount }).map((_, index) => {
            const isFinished = index < completedCount

            return (
              <Star
                key={index}
                aria-hidden="true"
                fill={isFinished ? 'currentColor' : 'none'}
                className={`h-[26px] w-[26px] transition-colors ${
                  isComplete
                    ? 'text-white'
                    : isFinished
                    ? 'text-primary-500'
                    : 'text-gray-300'
                }`}
              />
            )
          })}
        </div>

        <div className="flex justify-start">
          <Button
            type="alt"
            size="md"
            className={`h-[50px] ${isComplete ? '!border-white !bg-white !text-primary-700' : ''}`}
            disabled={isLoading}
            onClick={onRefresh}
          >
            <span className="flex items-center gap-md">
              <RefreshCw aria-hidden="true" className={`h-[16px] w-[16px] ${isLoading ? 'animate-spin' : ''}`} />
              <span className="flex flex-col items-start gap-[1px]">
                <span>Check progress</span>
                <span className="text-text-xs font-normal text-primary-700">
                  {lastCheckedAt
                    ? `Last checked ${lastCheckedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : 'Not checked yet'}
                </span>
              </span>
            </span>
          </Button>
        </div>
      </div>
    </div>
  </section>
)

export default OnboardingProgress
